/**
 * TomTom Traffic Density Service
 * Integrates with TomTom Traffic Stats API for historical traffic density analysis.
 * API Docs: https://developer.tomtom.com/traffic-stats/documentation/api/traffic-density
 */

// Import Google generator
import { generateMockTrafficDataGoogle } from "./trafficDensityGoogle";

// Removed Google API import - using OpenStreetMap instead to avoid CORS issues
const BASE_URL = "/proxy/tomtom-api/traffic/trafficstats";

// Demo mode: use mock data when API key lacks Traffic Stats access (403 errors)
const DEMO_MODE = true;

export interface TrafficDensityParams {
	jobName: string;
	bbox: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
	dateFrom: string; // YYYY-MM-DD
	dateTo: string; // YYYY-MM-DD
	timeSets: ("morning" | "afternoon" | "evening")[];
}

export interface JobStatus {
	jobId: string;
	state: "ACCEPTED" | "RUNNING" | "DONE" | "FAILED" | "DELETED";
	message?: string;
	resultUrl?: string;
}

/**
 * Convert bbox [minLon, minLat, maxLon, maxLat] to GeoJSON Polygon
 */
function bboxToPolygon(bbox: [number, number, number, number]): any {
	const [minLon, minLat, maxLon, maxLat] = bbox;
	return {
		type: "MultiPolygon",
		coordinates: [
			[
				[
					[minLon, minLat],
					[maxLon, minLat],
					[maxLon, maxLat],
					[minLon, maxLat],
					[minLon, minLat],
				],
			],
		],
	};
}

/**
 * Build time sets based on selected periods
 */
function buildTimeSets(
	periods: ("morning" | "afternoon" | "evening")[],
): any[] {
	const allDays = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
	const sets: any[] = [];

	// New 8-hour shifts configuration:
	// Morning: 06:00 - 14:00
	// Afternoon: 14:00 - 22:00
	// Evening/Night: 22:00 - 06:00 (split as 22-24 and 00-06)

	if (periods.includes("morning")) {
		sets.push({
			name: "Mattina",
			timeGroups: [{ days: allDays, times: ["06:00-14:00"] }],
		});
	}
	if (periods.includes("afternoon")) {
		sets.push({
			name: "Pomeriggio",
			timeGroups: [{ days: allDays, times: ["14:00-22:00"] }],
		});
	}
	if (periods.includes("evening")) {
		sets.push({
			name: "Sera",
			timeGroups: [{ days: allDays, times: ["22:00-24:00", "00:00-06:00"] }],
		});
	}

	return sets;
}

/**
 * Generate realistic mock traffic density data using real road geometries from OpenStreetMap
 */
async function generateMockTrafficData(
	bbox: [number, number, number, number],
	timeSets: string[] = ["Mattina", "Pomeriggio", "Sera"],
): Promise<any> {
	const [minLon, minLat, maxLon, maxLat] = bbox;

	// Overpass API query to get roads in the bbox
	// Filter for major roads: motorway, trunk, primary, secondary, tertiary
	const query = `
        [out:json][timeout:25];
        (
            way["highway"~"^(motorway|trunk|primary|secondary|tertiary)$"]
                (${minLat},${minLon},${maxLat},${maxLon});
        );
        out geom;
    `;

	try {
		const response = await fetch("https://overpass-api.de/api/interpreter", {
			method: "POST",
			body: query,
		});

		if (!response.ok) {
			throw new Error(`Overpass API error: ${response.status}`);
		}

		const data = await response.json();
		const features: any[] = [];

		// Convert OSM ways to GeoJSON features with mock traffic data
		data.elements.forEach((way: any, index: number) => {
			if (!way.geometry || way.geometry.length < 2) return;

			// Convert OSM geometry to GeoJSON LineString
			const coordinates = way.geometry.map((node: any) => [node.lon, node.lat]);

			// Generate mock traffic properties
			const roadTypes = [
				"motorway",
				"trunk",
				"primary",
				"secondary",
				"tertiary",
			];
			const roadType = way.tags?.highway || "unknown";
			const frc = roadTypes.indexOf(roadType);

			// Higher traffic on major roads
			const baseDensity =
				frc === 0 ? 1500 : frc === 1 ? 1000 : frc === 2 ? 600 : 300;
			const sampleSize = Math.floor(baseDensity + Math.random() * 500);

			features.push({
				type: "Feature",
				geometry: {
					type: "LineString",
					coordinates,
				},
				properties: {
					roadName:
						way.tags?.name ||
						`${roadType.charAt(0).toUpperCase() + roadType.slice(1)} ${index}`,
					frc: frc >= 0 ? frc : 4,
					length: (Math.random() * 3).toFixed(2),
					sampleSize: sampleSize,
					averageSpeed: (40 + Math.random() * 60).toFixed(1),
					timeSet: timeSets[index % timeSets.length], // Distribute among selected periods
					highway: roadType,
				},
			});
		});

		console.log(
			`[TrafficDensity] Generated ${features.length} road segments from OSM`,
		);

		return {
			type: "FeatureCollection",
			features: features.slice(0, 100), // Limit to 100 roads for performance
		};
	} catch (error) {
		console.error(
			"[TrafficDensity] OSM fetch failed, using fallback random data:",
			error,
		);

		// Fallback to simple random data if OSM fails
		const features: any[] = [];
		for (let i = 0; i < 30; i++) {
			const startLon = minLon + Math.random() * (maxLon - minLon);
			const startLat = minLat + Math.random() * (maxLat - minLat);
			const endLon = startLon + (Math.random() - 0.5) * 0.01;
			const endLat = startLat + (Math.random() - 0.5) * 0.01;

			features.push({
				type: "Feature",
				geometry: {
					type: "LineString",
					coordinates: [
						[startLon, startLat],
						[endLon, endLat],
					],
				},
				properties: {
					roadName: `Via ${
						["Roma", "Milano", "Napoli", "Torino", "Venezia"][i % 5]
					} ${i}`,
					frc: Math.floor(Math.random() * 5),
					length: (Math.random() * 2).toFixed(2),
					sampleSize: Math.floor(Math.random() * 2000),
					averageSpeed: (30 + Math.random() * 70).toFixed(1),
					timeSet: timeSets[i % timeSets.length], // Distribute among selected periods
				},
			});
		}

		return {
			type: "FeatureCollection",
			features,
		};
	}
}

/**
 * Submit a Traffic Density job to TomTom API
 * Returns the jobId for polling
 */
export async function submitDensityJob(
	params: TrafficDensityParams,
): Promise<string> {
	// Demo mode: return mock job ID immediately
	if (DEMO_MODE) {
		console.log("[TrafficDensity] DEMO MODE: Using mock data");
		return "demo-job-" + Date.now();
	}

	const url = `${BASE_URL}/trafficdensity/1`;

	const body = {
		jobName: params.jobName,
		distanceUnit: "KILOMETERS",
		network: {
			name: params.jobName.replace(/\s+/g, "_"),
			geometry: bboxToPolygon(params.bbox),
			timeZoneId: "Europe/Rome",
			frcs: ["0", "1", "2", "3", "4"], // Highways + major roads
			probeSource: "ALL",
		},
		dateRange: {
			name: "Analysis Period",
			from: params.dateFrom,
			to: params.dateTo,
			exclusions: [],
		},
		timeSets: buildTimeSets(params.timeSets),
	};

	console.log("[TrafficDensity] Submitting job:", body);

	const res = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	if (!res.ok) {
		const errText = await res.text();
		console.error("[TrafficDensity] Submit error:", res.status, errText);
		throw new Error(`TomTom API Error: ${res.status} - ${errText}`);
	}

	const data = await res.json();
	console.log("[TrafficDensity] Job submitted:", data);

	// TomTom returns { jobId: "..." }
	return data.jobId;
}

/**
 * Check the status of a Traffic Density job
 */
export async function checkJobStatus(jobId: string): Promise<JobStatus> {
	const url = `${BASE_URL}/status/1/${jobId}`;

	const res = await fetch(url);

	if (!res.ok) {
		throw new Error(`Status check failed: ${res.status}`);
	}

	const data = await res.json();
	console.log("[TrafficDensity] Job status:", data);

	return {
		jobId: data.jobId,
		state: data.state,
		message: data.message,
		resultUrl: data.urls?.resultUrl,
	};
}

/**
 * Download the GeoJSON results of a completed job
 */
export async function downloadResults(resultUrl: string): Promise<any> {
	// Riscrive l'URL TomTom per passare dal proxy nginx (che aggiunge la chiave)
	const url = resultUrl.replace("https://api.tomtom.com", "/proxy/tomtom-api");

	console.log("[TrafficDensity] Downloading results from:", url);

	const res = await fetch(url);

	if (!res.ok) {
		throw new Error(`Download failed: ${res.status}`);
	}

	return res.json();
}

/**
 * Poll for job completion with configurable interval
 * Returns the result URL when done
 */
export async function waitForJob(
	jobId: string,
	onProgress?: (status: JobStatus) => void,
	intervalMs = 5000,
	maxAttempts = 60, // 5 minutes max
	bbox?: [number, number, number, number], // For demo mode
	timeSets?: string[], // For demo mode
): Promise<string | any> {
	// Demo mode: simulate processing and return mock data
	if (DEMO_MODE && jobId.startsWith("demo-job-")) {
		// Simulate "RUNNING" state
		if (onProgress) {
			onProgress({ jobId, state: "RUNNING", message: "Inizializzazione..." });
		}
		await new Promise((resolve) => setTimeout(resolve, 800));

		// Convert timeSets from English to Italian
		const timeSetMap: Record<string, string> = {
			morning: "Mattina",
			afternoon: "Pomeriggio",
			evening: "Sera",
		};
		const italianTimeSets = timeSets?.map((ts) => timeSetMap[ts] || ts) || [
			"Mattina",
			"Pomeriggio",
			"Sera",
		];

		if (onProgress) {
			onProgress({
				jobId,
				state: "RUNNING",
				message: "Caricamento strade (Google Roads)...",
			});
		}

		// Try Google Roads first
		let mockData = bbox
			? await generateMockTrafficDataGoogle(bbox, italianTimeSets)
			: { type: "FeatureCollection", features: [] };
		let source = "Google Roads";

		// Fallback to OSM if Google returns no features (e.g. no key or error)
		if (!mockData.features || mockData.features.length === 0) {
			if (onProgress) {
				onProgress({
					jobId,
					state: "RUNNING",
					message: "Google fallito, provo OpenStreetMap...",
				});
			}
			console.log(
				"[TrafficDensity] Google returned no data, falling back to OSM",
			);
			mockData = bbox
				? await generateMockTrafficData(bbox, italianTimeSets)
				: { type: "FeatureCollection", features: [] };
			source = "OSM";
		}

		if (onProgress) {
			onProgress({
				jobId,
				state: "DONE",
				message: `Completato (demo con ${source})`,
			});
		}

		return mockData;
	}

	let attempts = 0;

	while (attempts < maxAttempts) {
		const status = await checkJobStatus(jobId);

		if (onProgress) {
			onProgress(status);
		}

		if (status.state === "DONE") {
			if (!status.resultUrl) {
				throw new Error("Job completed but no result URL provided");
			}
			return status.resultUrl;
		}

		if (status.state === "FAILED") {
			throw new Error(`Job failed: ${status.message || "Unknown error"}`);
		}

		if (status.state === "DELETED") {
			throw new Error("Job was deleted");
		}

		// Wait before next poll
		await new Promise((resolve) => setTimeout(resolve, intervalMs));
		attempts++;
	}

	throw new Error("Job timed out after maximum polling attempts");
}
